import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { labelTemplateApi } from '@/lib/api/services/label-template.api';
import type { LabelFieldKey, LabelSizeKey } from '@/types/label-template';

export const labelTemplateKeys = {
  all: ['label-templates'] as const,
  tenant: () => [...labelTemplateKeys.all, 'tenant'] as const,
  recipe: (recipeId: number | string) =>
    [...labelTemplateKeys.all, 'recipe', String(recipeId)] as const,
};

/** The tenant's label template — what every label prints unless a recipe differs. */
export function useLabelTemplate(enabled = true) {
  return useQuery({
    queryKey: labelTemplateKeys.tenant(),
    queryFn: () => labelTemplateApi.get(),
    enabled,
  });
}

/**
 * What one recipe's label prints, tenant template and override already resolved.
 *
 * Saving the tenant template invalidates every recipe's copy: the whole point of
 * T-1001 is that a recipe's label changes when the tenant's does, without anyone
 * opening the recipe.
 */
export function useRecipeLabelTemplate(recipeId: number | string, enabled = true) {
  return useQuery({
    queryKey: labelTemplateKeys.recipe(recipeId),
    queryFn: () => labelTemplateApi.getForRecipe(recipeId),
    enabled: enabled && recipeId !== '' && recipeId != null,
  });
}

export function useUpdateLabelTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ fields, size }: { fields: LabelFieldKey[]; size?: LabelSizeKey }) =>
      labelTemplateApi.update(fields, size),
    onSuccess: () => {
      // Everything under `label-templates`, tenant and recipes alike.
      queryClient.invalidateQueries({ queryKey: labelTemplateKeys.all });
    },
  });
}

export function useUpdateRecipeLabelTemplate(recipeId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fields: LabelFieldKey[]) => labelTemplateApi.updateForRecipe(recipeId, fields),
    onSuccess: (data) => {
      queryClient.setQueryData(labelTemplateKeys.recipe(recipeId), data);
    },
  });
}
